// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

import { useCallback, useEffect, useRef } from 'react';
import * as stylex from '@stylexjs/stylex';
import { Button } from "../Button/index.js";
import { Icon } from "../Icon/index.js";
import "../theme/tokens.stylex.js";
import { colorVars, spacingVars, radiusVars, durationVars, easeVars, shadowVars, typographyVars, typeScaleDefaults } from "../theme/tokens.stylex.js";
import { mergeProps } from "../utils/index.js";
import { useTheme } from "../theme/index.js";
import { MediaTheme } from "../theme/MediaTheme.js";
import { themeProps } from "../utils/themeProps.js";
import { useTranslator } from "../i18n/index.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Individual toast notification.
 *
 * Renders with inverted surface colors for the default variant,
 * and error-inverted for the error variant. Uses MediaTheme
 * to set the correct token context for children. Pauses auto-dismiss
 * on hover and focus.
 *
 * @example
 * ```
 * <Toast
 *   type="info"
 *   body="Saved successfully"
 *   isAutoHide={true}
 *   autoHideDuration={5000}
 *   onDismiss={(reason) => removeToast(id, reason)}
 * />
 * ```
 */
export function Toast({
  type,
  body,
  endContent,
  isAutoHide,
  autoHideDuration,
  isExiting = false,
  onDismiss
}) {
  const t = useTranslator();
  const timerRef = useRef(null);
  const isPausedRef = useRef(false);
  const remainingRef = useRef(autoHideDuration);
  // Will be initialized by startTimer when actually used
  const startTimeRef = useRef(null);

  // Read onDismiss through a ref: the viewport re-creates it on every render
  // (another toast arriving/exiting), and a startTimer that depends on it
  // would restart — and un-pause — this toast's timer on unrelated renders.
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;
  const startTimer = useCallback(() => {
    if (!isAutoHide || isPausedRef.current) {
      return;
    }
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    startTimeRef.current = Date.now();
    timerRef.current = setTimeout(() => {
      onDismissRef.current('auto');
    }, remainingRef.current);
  }, [isAutoHide]);
  const pauseTimer = useCallback(() => {
    if (!isAutoHide || isPausedRef.current) {
      return;
    }
    isPausedRef.current = true;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (startTimeRef.current != null) {
      const elapsed = Date.now() - startTimeRef.current;
      remainingRef.current = Math.max(remainingRef.current - elapsed, 1000);
    }
  }, [isAutoHide]);
  const resumeTimer = useCallback(() => {
    if (!isAutoHide || !isPausedRef.current) {
      return;
    }
    isPausedRef.current = false;
    startTimer();
  }, [isAutoHide, startTimer]);
  useEffect(() => {
    remainingRef.current = autoHideDuration;
    startTimer();
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
    // startTimer's identity is stable per isAutoHide, so this runs on mount
    // and on a genuine duration change — not on unrelated viewport renders.
  }, [autoHideDuration, startTimer]);

  // Pause the auto-hide timer while the window is not focused, so a toast
  // doesn't silently expire while the user is in another window or tab.
  useEffect(() => {
    if (!isAutoHide) {
      return;
    }
    window.addEventListener('blur', pauseTimer);
    window.addEventListener('focus', resumeTimer);
    return () => {
      window.removeEventListener('blur', pauseTimer);
      window.removeEventListener('focus', resumeTimer);
    };
  }, [isAutoHide, pauseTimer, resumeTimer]);
  const handleDismiss = useCallback(() => {
    onDismiss('manual');
  }, [onDismiss]);
  const isError = type === 'error';
  // Determine media mode: inverted surface is always dark in light mode,
  // always light in dark mode. Error toast is always on a dark surface.
  const {
    mode
  } = useTheme();
  const mediaMode = isError || mode === 'light' ? 'dark' : 'light';
  return /*#__PURE__*/_jsx("div", {
    role: isError ? 'alert' : 'status',
    "aria-live": isError ? 'assertive' : 'polite',
    "aria-atomic": "true",
    onMouseEnter: pauseTimer,
    onMouseLeave: resumeTimer,
    onFocusCapture: pauseTimer,
    onBlurCapture: resumeTimer,
    ...mergeProps(themeProps('toast', {
      type
    }), {
      0: {
        className: "x1na6nto x1pzlopt x1hviunn x1l2rt3b xxtfdz8 x14hfi27 x1hc1fzr x9ynric xjm74w1 xw6l6zx xnn1q72 x6o7n8i xuedmi6 xsagj69 xlr8y92 x4itv7f x1tswlu5 xenstv7"
      },
      2: {
        className: "x1na6nto x1pzlopt x1hviunn x1l2rt3b xxtfdz8 x14hfi27 x1hc1fzr x9ynric xjm74w1 xw6l6zx xnn1q72 x6o7n8i xuedmi6 xsagj69 xlr8y92 x4itv7f x1tswlu5 x1l5t2a3"
      },
      1: {
        className: "x1na6nto x1pzlopt x1hviunn x1l2rt3b xxtfdz8 x14hfi27 x9ynric xjm74w1 xw6l6zx x6o7n8i xuedmi6 xsagj69 xlr8y92 x4itv7f x1tswlu5 xenstv7 xg01cxk x1lm5u"
      },
      3: {
        className: "x1na6nto x1pzlopt x1hviunn x1l2rt3b xxtfdz8 x14hfi27 x9ynric xjm74w1 xw6l6zx x6o7n8i xuedmi6 xsagj69 xlr8y92 x4itv7f x1tswlu5 x1l5t2a3 xg01cxk x1lm5u"
      }
    }[!!isError << 1 | !!isExiting << 0]),
    children: /*#__PURE__*/_jsx(MediaTheme, {
      mode: mediaMode,
      children: /*#__PURE__*/_jsxs("div", {
        ...{
          className: "x78zum5 x1cy8zhl xjcht0a xh8yej3"
        },
        children: [/*#__PURE__*/_jsx("div", {
          ...{
            className: "x98rzlu xeuugli"
          },
          children: body
        }), /*#__PURE__*/_jsxs("div", {
          ...{
            className: "x2lah0s x78zum5 x6s0dn4 x1txdalj xacfrgf xmequkd"
          },
          children: [endContent, /*#__PURE__*/_jsx(Button, {
            variant: "ghost",
            size: "sm",
            icon: /*#__PURE__*/_jsx(Icon, {
              icon: "close",
              size: "sm",
              color: "inherit"
            }),
            label: t('@astryx.toast.dismiss'),
            onClick: handleDismiss,
            isIconOnly: true
          })]
        })]
      })
    })
  });
}
Toast.displayName = 'Toast';